import React from 'react';
import {
  Box,
  Divider,
  FormControl,
  InputLabel,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import PersonAddRoundedIcon from '@mui/icons-material/PersonAddRounded';
import { useTranslation } from 'react-i18next';
import type { HostBookingClientOption } from '../../models/HostOrganizationUser';
import {
  isManualBookingNewClient,
  MANUAL_BOOKING_NEW_CLIENT,
} from '../../utils/manualHostBooking';
import BookingClientOptionRow from './BookingClientOptionRow';

function organizationPersonName(client: HostBookingClientOption): string | null {
  const first = client.studentProfile?.firstName?.trim() ?? '';
  const last = client.studentProfile?.lastName?.trim() ?? '';
  const combined = `${first} ${last}`.trim();
  return combined || null;
}

function clientRowLabels(client: HostBookingClientOption, unnamedLabel: string) {
  const accountName = client.name?.trim() || '';
  const schoolName = organizationPersonName(client);
  const phone = client.phoneNumber?.trim() || '';
  const name = accountName || schoolName || phone || unnamedLabel;
  const organizationName = schoolName && schoolName !== name ? schoolName : null;
  return { name, organizationName };
}

export interface BookingClientSelectProps {
  label: string;
  unnamedLabel: string;
  clients: HostBookingClientOption[];
  value: string;
  onChange: (id: string) => void;
  loading?: boolean;
  disabled?: boolean;
  sx?: object;
}

const BookingClientSelect: React.FC<BookingClientSelectProps> = ({
  label,
  unnamedLabel,
  clients,
  value,
  onChange,
  loading = false,
  disabled = false,
  sx,
}) => {
  const { t } = useTranslation();
  const newClientLabel = t('spaceCalendar.noBookingClientGuest', 'New client');
  const blockedLabel = t('spaceCalendar.studentBookingBlocked', 'Cannot book');

  return (
    <FormControl fullWidth sx={sx}>
      <InputLabel>{label}</InputLabel>
      <Select
        value={value}
        onChange={(e) => onChange(String(e.target.value))}
        label={label}
        disabled={loading || disabled}
        MenuProps={{
          PaperProps: {
            sx: { maxHeight: 360 },
          },
        }}
        sx={{
          '& .MuiSelect-select': {
            display: 'flex',
            alignItems: 'center',
            minHeight: '1.4375em',
          },
        }}
        renderValue={(selected) => {
          if (isManualBookingNewClient(selected)) {
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonAddRoundedIcon sx={{ fontSize: 18, color: '#4E67EB' }} />
                <Typography component="span" sx={{ color: '#4E67EB' }}>
                  {newClientLabel}
                </Typography>
              </Box>
            );
          }
          const client = clients.find((row) => row.id === selected);
          const labels = client
            ? clientRowLabels(client, unnamedLabel)
            : { name: unnamedLabel, organizationName: null };
          return (
            <BookingClientOptionRow
              name={labels.name}
              organizationName={labels.organizationName}
              profileImageUrl={client?.profileImageUrl}
              bandName={client?.bandName}
              compact
            />
          );
        }}
      >
        <MenuItem
          value={MANUAL_BOOKING_NEW_CLIENT}
          sx={{
            color: '#4E67EB',
            py: 1.25,
            '& .MuiListItemIcon-root': {
              minWidth: 36,
              color: '#4E67EB',
            },
          }}
        >
          <ListItemIcon>
            <PersonAddRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={newClientLabel} />
        </MenuItem>
        {clients.length > 0 ? <Divider sx={{ my: 0.5 }} /> : null}
        {clients.map((client) => {
          const blocked = Boolean(client.moderationWarning?.bookingBlocked);
          const { name, organizationName } = clientRowLabels(client, unnamedLabel);
          return (
            <MenuItem
              key={client.id}
              value={client.id}
              disabled={blocked}
              sx={{ py: 1.25, whiteSpace: 'normal', alignItems: 'flex-start' }}
            >
              <BookingClientOptionRow
                name={name}
                organizationName={organizationName}
                phoneNumber={client.phoneNumber}
                profileImageUrl={client.profileImageUrl}
                bandName={client.bandName}
                blockedLabel={blocked ? blockedLabel : null}
              />
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};

export default BookingClientSelect;
